import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "../../lib/api";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  UserPlus, Search, Loader2, Star, ArrowUp, ArrowDown, XCircle, Receipt,
  Stethoscope, CheckCircle2, ListOrdered, RotateCcw, IndianRupee, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { queueAction, checkInPatient, reorderQueue } from "../../lib/queueApi";
import { TokenBadge, PriorityFlag, StatusBadge, BillingBadge, fmtTime, waitedMinutes } from "./QueueBits";
import CollectPaymentDialog from "./CollectPaymentDialog";

/**
 * Reception view — queue preparation and money collection.
 * Deliberately has no CALL NEXT / START / COMPLETE: the doctor owns those.
 */
export default function ReceptionQueueView({ board, doctors, defaultFee, canManage = true, onChanged }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [checkInDoctor, setCheckInDoctor] = useState(doctors?.[0]?.user_id || "");
  const [priority, setPriority] = useState(false);
  const [busy, setBusy] = useState("");
  const [payFor, setPayFor] = useState(null);

  useEffect(() => {
    if (!checkInDoctor && doctors?.length) setCheckInDoctor(doctors[0].user_id);
  }, [doctors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/patients", { params: { q: search.trim(), limit: 6 } });
        if (!cancelled) setResults(data.items || []);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search]);

  const waiting = board?.waiting || [];
  const called = board?.called || [];
  const inConsultation = board?.in_consultation || [];
  const completed = board?.completed || [];
  const skipped = board?.skipped || [];
  const billingPending = board?.billing_pending || [];
  const pendingTotal = useMemo(
    () => billingPending.reduce((sum, e) => sum + (e.invoice ? Number(e.invoice.balance_due || 0) : 0), 0),
    [billingPending]
  );

  const run = async (label, fn) => {
    setBusy(label);
    const res = await fn();
    setBusy("");
    if (res?.ok) onChanged && onChanged();
    return res;
  };

  const doCheckIn = async (patient) => {
    const res = await run("checkin", () =>
      checkInPatient({
        patient_id: patient.patient_id,
        doctor_id: checkInDoctor || null,
        reason: "",
        priority,
      })
    );
    if (res.ok) {
      toast.success(`${patient.name} checked in · token ${res.data.queue_number}`);
      setSearch("");
      setResults([]);
      setPriority(false);
    }
  };

  const move = async (entry, direction) => {
    const ids = waiting.map((e) => e.appointment_id);
    const idx = ids.indexOf(entry.appointment_id);
    const swapWith = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || swapWith < 0 || swapWith >= ids.length) return;
    [ids[idx], ids[swapWith]] = [ids[swapWith], ids[idx]];
    await run("reorder", () => reorderQueue(ids));
  };

  return (
    <div className="space-y-6" data-testid="reception-queue-view">
      {/* Check-in */}
      <Card className="border-slate-200" data-testid="reception-checkin-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="w-4 h-4 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Check in a patient</h3>
          </div>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patient by name or phone…"
                className="pl-9 h-11"
                data-testid="reception-patient-search"
              />
              {searching && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-300" />}
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-testid="reception-search-results">
                  {results.map((p) => (
                    <button
                      key={p.patient_id}
                      type="button"
                      onClick={() => doCheckIn(p)}
                      disabled={!!busy}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-blue-50 transition-colors text-left"
                      data-testid={`reception-checkin-${p.patient_id}`}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-slate-900 truncate">{p.name}</span>
                        <span className="block text-xs text-slate-400">{p.phone} · {p.age}y {p.gender}</span>
                      </span>
                      <span className="text-xs font-semibold text-blue-600 flex-shrink-0">Check in</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Select value={checkInDoctor} onValueChange={setCheckInDoctor}>
              <SelectTrigger className="w-full lg:w-56 h-11" data-testid="reception-checkin-doctor">
                <SelectValue placeholder="Assign doctor" />
              </SelectTrigger>
              <SelectContent>
                {(doctors || []).map((d) => (
                  <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant={priority ? "default" : "outline"}
              onClick={() => setPriority((v) => !v)}
              className={priority ? "bg-rose-600 hover:bg-rose-700 h-11" : "h-11"}
              data-testid="reception-priority-toggle"
            >
              <Star className={`w-4 h-4 mr-2 ${priority ? "fill-white" : ""}`} /> Priority
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Counts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Waiting", value: board?.counts?.waiting || 0, tone: "text-blue-700", tid: "count-waiting" },
          { label: "With doctor", value: (board?.counts?.called || 0) + (board?.counts?.in_consultation || 0), tone: "text-emerald-700", tid: "count-with-doctor" },
          { label: "Consultation done", value: board?.counts?.completed || 0, tone: "text-slate-700", tid: "count-completed" },
          { label: "Billing pending", value: board?.counts?.billing_pending || 0, tone: "text-amber-700", tid: "count-billing-pending" },
        ].map((c) => (
          <Card key={c.label} className="border-slate-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-slate-500">{c.label}</div>
              <div className={`text-2xl font-bold mt-1 ${c.tone}`} style={{ fontFamily: "Manrope" }} data-testid={c.tid}>
                {c.value}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Waiting + management */}
        <Card className="border-slate-200" data-testid="reception-waiting-list">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
              <ListOrdered className="w-4 h-4 text-blue-600" /> Waiting ({waiting.length})
            </h3>
            {waiting.length === 0 ? (
              <div className="text-center py-10 text-sm text-slate-400 italic" data-testid="reception-waiting-empty">
                Nobody waiting
              </div>
            ) : (
              <div className="space-y-2">
                {waiting.map((e, idx) => (
                  <div
                    key={e.appointment_id}
                    className="p-3 rounded-lg border border-slate-200"
                    data-testid={`reception-waiting-row-${e.appointment_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                          {idx + 1}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate">{e.patient_name}</div>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <TokenBadge entry={e} />
                            {e.priority && <PriorityFlag />}
                            <span className="text-xs text-slate-400 truncate">{e.doctor_name}</span>
                            {waitedMinutes(e) != null && <span className="text-xs text-slate-400">· {waitedMinutes(e)}m</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0">
                        {canManage && (
                          <>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!!busy || idx === 0}
                              onClick={() => move(e, "up")} data-testid={`reception-up-${e.appointment_id}`} title="Move up">
                              <ArrowUp className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!!busy || idx === waiting.length - 1}
                              onClick={() => move(e, "down")} data-testid={`reception-down-${e.appointment_id}`} title="Move down">
                              <ArrowDown className="w-3.5 h-3.5 text-slate-500" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!!busy}
                              onClick={() => run("priority", () => queueAction(e.appointment_id, "priority", { priority: !e.priority }))}
                              data-testid={`reception-priority-${e.appointment_id}`} title="Toggle priority">
                              <Star className={`w-3.5 h-3.5 ${e.priority ? "fill-rose-500 text-rose-500" : "text-slate-400"}`} />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={!!busy}
                          onClick={() => run("cancel", () => queueAction(e.appointment_id, "cancel"))}
                          data-testid={`reception-cancel-${e.appointment_id}`} title="Cancel">
                          <XCircle className="w-3.5 h-3.5 text-slate-400 hover:text-rose-600" />
                        </Button>
                      </div>
                    </div>
                    {canManage && (doctors || []).length > 1 && (
                      <div className="mt-2 pl-11">
                        <Select
                          value={e.doctor_id}
                          onValueChange={(v) => run("assign", () => queueAction(e.appointment_id, "assign-doctor", { doctor_id: v }))}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`reception-assign-${e.appointment_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {doctors.map((d) => (
                              <SelectItem key={d.user_id} value={d.user_id}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {skipped.length > 0 && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold mb-2">
                  Skipped ({skipped.length})
                </div>
                <div className="space-y-2">
                  {skipped.map((e) => (
                    <div key={e.appointment_id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-amber-50/50"
                      data-testid={`reception-skipped-row-${e.appointment_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.patient_name}</div>
                        <div className="text-xs text-slate-400">skipped {fmtTime(e.skipped_at)}</div>
                      </div>
                      <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!busy}
                        onClick={() => run("requeue", () => queueAction(e.appointment_id, "requeue"))}
                        data-testid={`reception-requeue-${e.appointment_id}`}>
                        <RotateCcw className="w-3 h-3 mr-1" /> Back to queue
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          {/* With the doctor */}
          <Card className="border-slate-200" data-testid="reception-with-doctor-list">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <Stethoscope className="w-4 h-4 text-emerald-600" /> With the doctor ({called.length + inConsultation.length})
              </h3>
              {called.length + inConsultation.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400 italic">No patient called yet</div>
              ) : (
                <div className="space-y-2">
                  {[...inConsultation, ...called].map((e) => (
                    <div key={e.appointment_id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-emerald-50/40"
                      data-testid={`reception-active-row-${e.appointment_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{e.patient_name}</div>
                        <div className="text-xs text-slate-400 truncate">{e.doctor_name}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <TokenBadge entry={e} />
                        <StatusBadge status={e.queue_status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing pending */}
          <Card className="border-amber-200" data-testid="reception-billing-pending">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-amber-600" /> Billing pending ({billingPending.length})
                </h3>
                {pendingTotal > 0 && (
                  <span className="text-sm font-semibold text-amber-700 flex items-center gap-1" data-testid="billing-pending-total">
                    <IndianRupee className="w-3.5 h-3.5" />
                    {formatINR(pendingTotal).replace("₹", "")} to collect
                  </span>
                )}
              </div>
              {billingPending.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400 italic" data-testid="billing-pending-empty">
                  Everything is settled
                </div>
              ) : (
                <div className="space-y-2">
                  {billingPending.map((e) => (
                    <div key={e.appointment_id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50/40"
                      data-testid={`billing-pending-row-${e.appointment_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{e.patient_name}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <BillingBadge invoice={e.invoice} />
                          {e.invoice && (
                            <span className="text-xs text-slate-500">
                              {formatINR(e.invoice.balance_due)} due of {formatINR(e.invoice.total)}
                            </span>
                          )}
                          <span className="text-xs text-slate-400">done {fmtTime(e.completed_at)}</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0"
                        onClick={() => setPayFor(e)}
                        data-testid={`collect-payment-${e.appointment_id}`}
                      >
                        <Receipt className="w-3.5 h-3.5 mr-1.5" /> Collect
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Completed */}
          <Card className="border-slate-200" data-testid="reception-completed-list">
            <CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Consultations done ({completed.length})
              </h3>
              {completed.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400 italic">Nothing finished yet</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-auto">
                  {completed.map((e) => (
                    <div key={e.appointment_id} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-slate-50"
                      data-testid={`reception-completed-row-${e.appointment_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{e.patient_name}</div>
                        <div className="text-xs text-slate-400">{fmtTime(e.completed_at)} · {e.doctor_name}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <BillingBadge invoice={e.invoice} />
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                          onClick={() => navigate(`/patients/${e.patient_id}`)}
                          data-testid={`reception-open-patient-${e.appointment_id}`} title="Open patient">
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <CollectPaymentDialog
        open={!!payFor}
        onOpenChange={(v) => !v && setPayFor(null)}
        entry={payFor}
        defaultFee={defaultFee}
        onCompleted={() => {
          setPayFor(null);
          onChanged && onChanged();
        }}
      />
    </div>
  );
}
